import { supportSessionOrgId } from '@/lib/support-session'
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  DroidMapRolePermissions,
  Permission,
  PermissionsServiceClient,
  Role,
  UserGrant as ServiceUserGrant,
} from '@droidmap/permissions-service-contract'
import { processAllPages } from '@droidmap/shared-service-contract'

import { useAuth } from './AuthContext'
import { useClient } from './ClientContext'

/**
 * Get all permissions for a given role
 */
export const getPermissionsForRole = (role: Role): Permission[] => {
  const rolePermissions = DroidMapRolePermissions.get(role)
  return rolePermissions?.permissions ?? []
}

// Utility function to transform service UserGrant to local UserGrant
const transformServiceUserGrant = (
  serviceGrant: ServiceUserGrant,
): UserGrant => {
  return {
    id: `${serviceGrant.userId}-${serviceGrant.resourceId}-${serviceGrant.role}`,
    userId: serviceGrant.userId,
    resourceId: serviceGrant.resourceId,
    resourceType:
      serviceGrant.resourceType === 'organisation'
        ? 'organization'
        : (serviceGrant.resourceType as 'organization' | 'project'),
    role: serviceGrant.role,
    permissions: getPermissionsForRole(serviceGrant.role),
  }
}

export interface UserGrant {
  id: string
  userId: string
  resourceId: string
  resourceType: 'organization' | 'project'
  role: Role
  permissions: Permission[]
}

export interface PermissionContext {
  orgId: string | null
  projectId: string | null
}

export interface PermissionsState {
  grants: UserGrant[]
  context: PermissionContext
  isLoading: boolean
  isError: boolean
}

export interface PermissionsActions {
  setContext: (orgId: string | null, projectId: string | null) => void
  hasPermission: (permission: Permission, resourceId?: string) => boolean
  hasAnyPermission: (permissions: Permission[], resourceId?: string) => boolean
  hasRole: (
    role: Role,
    resourceType: 'organization' | 'project',
    resourceId?: string,
  ) => boolean
  refreshPermissions: () => Promise<void>
}

export type UsePermissions = PermissionsState & PermissionsActions

export const PermissionsContext = createContext<UsePermissions | undefined>(
  undefined,
)

const initialPermissionsState: PermissionsState = {
  grants: [],
  context: { orgId: null, projectId: null },
  isLoading: false,
  isError: false,
}

/**
 * Loads a user's grants and stores the *outcome* only.
 *
 * Entering the loading state is the caller's job, because the two callers enter
 * it at different moments: the render-time reset in `useProvidePermissions`
 * does it in the same render that first sees a new identity, while
 * `refreshPermissions` does it when the user asks.
 *
 * It lives at module scope rather than in a `useCallback` so that the effect
 * which calls it contains no setState the compiler can see through and inline —
 * every write here happens after the first `await`, but the compiler only
 * treats that as asynchronous across an opaque call boundary.
 *
 * `isStale` lets a caller drop a response that a newer identity has superseded.
 */
const loadUserGrants = async (
  permissionsClient: PermissionsServiceClient,
  userId: string,
  updatePermissionsState: (newState: Partial<PermissionsState>) => void,
  isStale: () => boolean = () => false,
): Promise<void> => {
  // A support session's grants count only in the one org it works in, so
  // they are asked for there; anywhere else (the client's default `system`)
  // the services answer none, and every permission-gated page would hide.
  const sessionOrgId = supportSessionOrgId()
  const metadata = sessionOrgId ? { userId, orgId: sessionOrgId } : undefined
  try {
    // Fetch all user grants from the permissions service, handling pagination
    const allGrants = await processAllPages((controls) =>
      permissionsClient.listUserGrantsByUser(
        {
          userId,
          ...controls,
        },
        metadata,
      ),
    )

    if (isStale()) {
      return
    }

    updatePermissionsState({
      grants: allGrants.map(transformServiceUserGrant),
      isLoading: false,
      isError: false,
    })
  } catch (error) {
    console.error('Failed to fetch user permissions:', error)
    // Keep whatever grants we already hold rather than clearing them. This
    // is the only code path that (re)populates the list — a single effect
    // keyed on the user id, plus `refreshPermissions()` — so there is no
    // retry, and wiping on a transient failure would hide every
    // permission-gated surface in the app until a full page reload. On the
    // initial fetch there is nothing to keep and the list stays empty.
    //
    // The trade-off is at the one call site that refreshes in order to
    // *drop* grants (deleting an org): a failure there leaves a stale
    // grant behind. That grant's resource no longer exists, so nothing
    // routes to it, and the backend authorises independently of this list.
    if (isStale()) {
      return
    }

    updatePermissionsState({
      isLoading: false,
      isError: true,
    })
  }
}

export const PermissionsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const permissions = useProvidePermissions()

  return (
    <PermissionsContext.Provider value={permissions}>
      {children}
    </PermissionsContext.Provider>
  )
}

export const usePermissions = (): UsePermissions => {
  const context = useContext(PermissionsContext)
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider')
  }
  return context
}

const useProvidePermissions = (): UsePermissions => {
  const [permissionsState, setPermissionsState] = useState<PermissionsState>(
    initialPermissionsState,
  )
  const { userDetails, isAuthenticated } = useAuth()
  const { permissionsClient } = useClient()

  const updatePermissionsState = useCallback(
    (newState: Partial<PermissionsState>) => {
      setPermissionsState((prevState) => ({ ...prevState, ...newState }))
    },
    [],
  )

  // The identity whose grants `permissionsState` currently describes. Signing in,
  // switching user, and signing out are all just a change to this value.
  const activeUserId = isAuthenticated ? (userDetails?.id ?? null) : null

  const refreshPermissions = useCallback(async () => {
    if (!activeUserId) {
      return
    }
    updatePermissionsState({ isLoading: true, isError: false })
    await loadUserGrants(
      permissionsClient,
      activeUserId,
      updatePermissionsState,
    )
  }, [activeUserId, permissionsClient, updatePermissionsState])

  // Reset to the state that matches the new identity during render, not in an
  // effect. The effect version committed one render in which the *previous*
  // user's grants were exposed as though they belonged to the newly signed-in
  // user — with `isLoading: false`, so permission-gated UI rendered against
  // them — before the effect corrected it. Signing out had the same hole.
  // `undefined` (rather than null) as the initial value means the signed-out
  // case still runs once on mount, exactly as the effect did.
  const [syncedUserId, setSyncedUserId] = useState<string | null | undefined>(
    undefined,
  )
  if (activeUserId !== syncedUserId) {
    setSyncedUserId(activeUserId)
    updatePermissionsState(
      activeUserId
        ? { isLoading: true, isError: false }
        : { grants: [], isLoading: false, isError: false },
    )
  }

  // Fetch permissions when user changes
  useEffect(() => {
    if (!activeUserId) {
      return
    }

    // Guards a slower fetch for a previous identity from landing on top of a
    // newer one — the old effect had no such guard.
    let cancelled = false

    void loadUserGrants(
      permissionsClient,
      activeUserId,
      updatePermissionsState,
      () => cancelled,
    )

    return () => {
      cancelled = true
    }
    // Keyed on the identity alone, as it always has been. `permissionsClient` is
    // deliberately excluded: it is not guaranteed to be referentially stable, and
    // including it re-runs the fetch on every render that produces a new client
    // object — an unbounded request loop, not a refresh. `refreshPermissions()`
    // is the supported way to re-read grants for the same user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUserId])

  const setContext = useCallback(
    (orgId: string | null, projectId: string | null) => {
      updatePermissionsState({
        context: { orgId, projectId },
      })
    },
    [updatePermissionsState],
  )

  const getRelevantGrants = useCallback(
    (resourceId?: string): UserGrant[] => {
      const { orgId, projectId } = permissionsState.context

      // If specific resourceId is provided, filter for it
      if (resourceId) {
        return permissionsState.grants.filter(
          (grant) => grant.resourceId === resourceId,
        )
      }

      // Otherwise, get grants for current context
      const relevantGrants = permissionsState.grants.filter((grant) => {
        if (
          projectId &&
          grant.resourceType === 'project' &&
          grant.resourceId === projectId
        ) {
          return true
        }
        if (
          orgId &&
          grant.resourceType === 'organization' &&
          grant.resourceId === orgId
        ) {
          return true
        }
        return false
      })

      return relevantGrants
    },
    [permissionsState.grants, permissionsState.context],
  )

  const hasPermission = useCallback(
    (permission: Permission, resourceId?: string): boolean => {
      const relevantGrants = getRelevantGrants(resourceId)

      return relevantGrants.some((grant) =>
        grant.permissions.includes(permission),
      )
    },
    [getRelevantGrants],
  )

  const hasAnyPermission = useCallback(
    (permissions: Permission[], resourceId?: string): boolean => {
      return permissions.some((permission) =>
        hasPermission(permission, resourceId),
      )
    },
    [hasPermission],
  )

  const hasRole = useCallback(
    (
      role: Role,
      resourceType: 'organization' | 'project',
      resourceId?: string,
    ): boolean => {
      const relevantGrants = getRelevantGrants(resourceId)

      return relevantGrants.some(
        (grant) => grant.resourceType === resourceType && grant.role === role,
      )
    },
    [getRelevantGrants],
  )

  const permissionsActions = useMemo(
    () => ({
      setContext,
      hasPermission,
      hasAnyPermission,
      hasRole,
      refreshPermissions,
    }),
    [setContext, hasPermission, hasAnyPermission, hasRole, refreshPermissions],
  )

  return {
    ...permissionsState,
    ...permissionsActions,
  }
}
